<template>
  <el-dialog
    :visible.sync="dialogVisible"
    :show-close="true"
    :close-on-click-modal="true"
    :modal-append-to-body="true"
    custom-class="forgot-dialog"
    width="480px"
    @close="handleClose"
  >
    <div class="dialog-header">
      <h3 class="dialog-title">{{ $t('forgot.title') }}</h3>
    </div>
    <el-form ref="forgotForm" :model="form" :rules="rules" class="forgot-form">
      <el-form-item prop="email">
        <el-input v-model="form.email" :placeholder="$t('forgot.emailPlaceholder')" autocomplete="off"></el-input>
      </el-form-item>
      <el-form-item class="code-form-item" prop="emailCode">
        <div class="code-row">
          <el-input v-model="form.emailCode" :placeholder="$t('forgot.codePlaceholder')" autocomplete="off"></el-input>
          <span class="get-code-btn" @click="handleGetCode">{{ countdown > 0 ? `${countdown}s` : $t('forgot.getCode') }}</span>
        </div>
      </el-form-item>
      <el-form-item prop="password">
        <el-input v-model="form.password" type="password" :placeholder="$t('forgot.passwordPlaceholder')" autocomplete="new-password"></el-input>
      </el-form-item>
      <el-form-item prop="validatePswd">
        <el-input v-model="form.validatePswd" type="password" :placeholder="$t('forgot.confirmPlaceholder')" autocomplete="new-password"></el-input>
      </el-form-item>
      <el-form-item>
        <el-button type="primary" class="submit-btn" round @click="handleSubmit">{{ $t('forgot.submit') }}</el-button>
      </el-form-item>
    </el-form>
  </el-dialog>
</template>

<script>
import debounce from 'lodash/debounce'
import { recoverPassword } from '@/api/base'

export default {
  name: 'ForgotPasswordDialog',
  props: {
    visible: { type: Boolean, default: false }
  },
  data() {
    return {
      form: { email: '', emailCode: '', password: '', validatePswd: '' },
      codeTimer: null,
      countdown: 0
    }
  },
  computed: {
    rules() {
      return {
        email: [
          { required: true, message: this.$t('validation.emailRequired'), trigger: 'blur' },
          {
            pattern: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
            message: this.$t('validation.emailFormat'),
            trigger: 'blur'
          }
        ],
        emailCode: [
          { required: true, message: this.$t('validation.captchaRequired'), trigger: 'blur' }
        ],
        password: [
          { required: true, message: this.$t('validation.passwordRequired'), trigger: 'blur' }
        ],
        validatePswd: [
          { required: true, message: this.$t('validation.confirmPasswordRequired'), trigger: 'blur' },
          {
            validator: (rule, value, callback) => {
              if (value && value !== this.form.password) {
                callback(new Error(this.$t('forgot.noMatch')))
              } else {
                callback()
              }
            },
            trigger: 'blur'
          }
        ]
      }
    },
    dialogVisible: {
      get() { return this.visible },
      set(val) { this.$emit('update:visible', val) }
    }
  },
  created() {
    this.handleSubmit = debounce(this.submitForgot, 500, {
      leading: true,
      trailing: false
    })
  },
  methods: {
    handleClose() {
      if (this.$refs.forgotForm) {
        this.$refs.forgotForm.clearValidate()
      }
      this.$emit('update:visible', false)
    },
    handleGetCode() {
      if (this.countdown > 0) return
      this.$refs.forgotForm.validateField('email', (errorMessage) => {
        if (errorMessage) return
        this.$emit('get-code', this.form.email)
        this.countdown = 60
        this.codeTimer = setInterval(() => {
          this.countdown--
          if (this.countdown <= 0) {
            clearInterval(this.codeTimer)
          }
        }, 1000)
      })
    },
    submitForgot() {
      this.$refs.forgotForm.validate((valid) => {
        if (!valid) return
        recoverPassword({
          email: this.form.email,
          emailCode: this.form.emailCode,
          password: this.form.password,
          validatePswd: this.form.validatePswd
        }).then(() => {
          this.$message.success('密码找回成功，请重新登录')
          this.$emit('submit', this.form)
          this.$emit('update:visible', false)
        }).catch(() => {})
      })
    }
  },
  beforeDestroy() {
    if (this.codeTimer) clearInterval(this.codeTimer)
    if (this.handleSubmit && typeof this.handleSubmit.cancel === 'function') {
      this.handleSubmit.cancel()
    }
  }
}
</script>

<style lang="scss" scoped>
.dialog-header {
  text-align: center;
  margin-bottom: 30px;
  .dialog-title {
    font-size: 20px;
    font-weight: 600;
    color: #0866FF;
  }
}
.forgot-form {
  padding: 0 20px;
  ::v-deep .el-input__inner {
    border: none;
    border-radius: 25px;
    background: #F8F8F8;
    padding-left: 16px;
    font-size: 14px;
    height: 44px;
    &:focus { border: none; }
    &:-webkit-autofill,
    &:-webkit-autofill:hover,
    &:-webkit-autofill:focus {
      -webkit-box-shadow: 0 0 0 1000px #F8F8F8 inset;
      transition: background-color 5000s ease-in-out 0s;
    }
  }
}
.code-form-item {
  ::v-deep .el-input__inner {
    background: transparent;
    border-radius: 0;
  }
}
.code-row {
  display: flex;
  align-items: center;
  background: #F8F8F8;
  border-radius: 25px;
  padding-right: 16px;
  .el-input { flex: 1; }
  .get-code-btn {
    margin-left: 12px;
    font-size: 14px;
    color: #0866FF;
    cursor: pointer;
    white-space: nowrap;
    &:hover { opacity: 0.8; }
  }
}
.submit-btn {
  width: 100%;
  background: #0866FF;
  border-color: #0866FF;
  font-size: 16px;
  padding: 12px 0;
}
</style>

<style lang="scss">
.forgot-dialog {
  border-radius: 25px !important;
  padding: 30px 40px;
  .el-dialog__header {
    padding: 0;
    position: relative;
  }
  .el-dialog__headerbtn {
    top: 0;
    right: 0;
    font-size: 20px;
    display: block;
    position: absolute;
    z-index: 1;
  }
  .el-dialog__body { padding: 0; }
}
</style>
